import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useNavigate, useLocation, Link } from 'react-router-dom';

import styles from './Login.module.css';
import logo1 from 'assets/images/logo1.jpeg';
import logo2 from 'assets/images/logo2.jpeg';
import logo3 from 'assets/images/logo3.jpeg';
import { Button, Headline, Input, LanguageSwitcher } from 'components/core';
import { PasswordInput } from 'components/molecules/PasswordInput';
import 'styles/global.css';
import { RouteConstants } from '../../../constants';

import { authState, apiErrorState } from 'states';
import { useUserService } from 'services';

export const Login = () => {
  const { t } = useTranslation();
  const auth = useRecoilValue(authState);
  const [apiError, setApiError] = useRecoilState(apiErrorState);
  const userService = useUserService();
  const navigate = useNavigate();
  const location = useLocation();
  const accessDeniedFromRedirect = (location.state as any)?.accessDenied === true;

  useEffect(() => {
    // redirect to home if already logged in
    if (Object.keys(auth).length) navigate(RouteConstants.dashboards);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  interface IFormValues {
    username: string;
    password: string;
  }

  // form validation rules
  const validationSchema = Yup.object().shape({
    username: Yup.string().required(t('AUTH.USERNAME_REQUIRED')).email(t('AUTH.USERNAME_INVALID')),
    password: Yup.string().required(t('AUTH.LOGIN_PASSWORD_REQUIRED')),
  });
  const formOptions = { resolver: yupResolver(validationSchema) };

  // get functions to build form with useForm() hook
  const { register, handleSubmit, formState } = useForm<IFormValues>(formOptions);
  const { errors, isSubmitting } = formState;

  const onSubmit = (values: IFormValues) => {
    setApiError('');
    return userService.login(values.username, values.password).catch((error: any) => {
      setApiError(error instanceof Error ? error.message : t('AUTH.LOGIN_INVALID_CREDENTIALS'));
    });
  };

  return (
    <div className="col-12 vh-100">
      <div className={styles.languageSwitcher}>
        <LanguageSwitcher />
      </div>
      <div className="vh-100">
        <div className="row m-0 p-0">
          <div className={`${styles.right_side_view} col-sm-12 col-md-6 col-lg-6 vh-100`}>
            <div className="d-flex justify-content-center h-100 align-items-center">
              <img
                src={logo1}
                alt={t('AUTH.LOGIN_LOGO_ALT')}
                className={`${styles.image_view} me-3 p-0`}
              />
              <img
                src={logo2}
                alt={t('AUTH.LOGIN_LOGO_ALT')}
                className={`${styles.image_view} me-3 p-0`}
              />
              <img
                src={logo3}
                alt={t('AUTH.LOGIN_LOGO_ALT')}
                className={`${styles.image_view} me-3 p-0`}
              />
            </div>
          </div>
          <div className={`${styles.left_side_view} col-sm-12 col-md-6 col-lg-6 vh-100`}>
            <div className="d-flex h-100 align-items-center justify-content-center">
              <div className="flex-column responsive-width-1">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="mb-5">
                    <Headline title={t('AUTH.LOGIN_TITLE')} />
                  </div>
                  <div className="mb-3">
                    <Input
                      type="text"
                      showOptTxt={false}
                      showDescription={false}
                      showTitle={true}
                      placeholder={t('AUTH.USERNAME_PLACEHOLDER')}
                      title={t('AUTH.USERNAME_LABEL')}
                      {...register('username')}
                      className={`${errors.username ? 'is-invalid' : ''}`}
                    />
                    <div className="invalid-feedback">{errors.username?.message}</div>
                  </div>
                  <div className="mb-4">
                    <PasswordInput
                      showOptTxt={false}
                      showDescription={false}
                      showTitle={true}
                      placeholder={t('AUTH.PASSWORD_PLACEHOLDER')}
                      title={t('AUTH.PASSWORD_LABEL')}
                      {...register('password')}
                      error={errors.password?.message}
                    />
                  </div>
                  <div className="mb-3">
                    <Button className="w-100" disabled={isSubmitting}>
                      {isSubmitting && (
                        <span className="spinner-border spinner-border-sm me-3"></span>
                      )}
                      {t('AUTH.LOGIN_BUTTON')}
                    </Button>
                  </div>
                  <div className="mb-2 text-center mt-4">
                    <Link className={styles.link_style} to={RouteConstants.forgotPassword}>
                      {t('AUTH.FORGOT_PASSWORD_LINK')}
                    </Link>
                  </div>
                  {accessDeniedFromRedirect && apiError.length === 0 && (
                    <div className="alert alert-danger mt-3 mb-0">
                      {t('AUTH.ACCESS_DENIED')}
                    </div>
                  )}
                  {apiError.length > 0 && (
                    <div className="alert alert-danger mt-3 mb-0">{`${apiError}`}</div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
